/* eslint-disable no-undef */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { TreeSelect, Select, Tag, Spin } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { MobileTreeSheet } from '@client/components/mobile';

// ============================================================================
// CONSTANTS
// ============================================================================
const DEFAULTS = {
  LANGUAGE: 'fr',
};

const CONFIG = {
  DROPDOWN_MAX_HEIGHT: 400,
  DROPDOWN_PADDING: '8px 0',
  CHEVRON_SIZE: 14,
  CACHE_DURATION: 15 * 60 * 1000, // 15 minutes cache
  SEARCH_DEBOUNCE: 300, // ms
  MIN_SEARCH_LENGTH: 2,
};

// Stable empty object to prevent infinite re-renders
const EMPTY_FILTER_PARAMS = {};

// API endpoint for the new optimized backend
const CALCULATOR_TREE_API = '/api/cultures/tree/calculator/';

// ============================================================================
// GLOBAL CACHE - Persists across component mounts and step navigations
// ============================================================================
const globalCache = {
  // Full tree cache per language
  fullTrees: new Map(),
  // Timestamp for cache validation
  timestamp: null,
};

// Generate cache key from language and filter params
const getCacheKey = (lang, filterParams = {}) => {
  const filterStr = Object.entries(filterParams)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return filterStr ? `${lang}:${filterStr}` : lang;
};

const getCachedFullTree = (lang, filterParams = {}) => {
  const cacheKey = getCacheKey(lang, filterParams);
  const cached = globalCache.fullTrees.get(cacheKey);
  if (cached && globalCache.timestamp && Date.now() - globalCache.timestamp < CONFIG.CACHE_DURATION) {
    return cached;
  }
  return null;
};

const setCachedFullTree = (lang, filterParams = {}, data) => {
  const cacheKey = getCacheKey(lang, filterParams);
  globalCache.fullTrees.set(cacheKey, data);
  globalCache.timestamp = Date.now();
};

// ============================================================================
// LANGUAGE SELECTOR COMPONENT
// ============================================================================
// Rule 14 (SP-408): globe icon + the word « Langue » must sit to the LEFT
// of the selector. Rule 17: clicking either the icon or the word opens
// the dropdown — Ant 5's `prefix` lives inside the Select selector so a
// click anywhere on it opens the popup natively.
const LanguageSelector = React.memo(({ value, options, onChange, langLabel }) => (
  <Select
    size="small"
    value={value}
    onChange={onChange}
    options={options}
    className="language-selector"
    prefix={
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <GlobalOutlined />
        <span>{langLabel}</span>
      </span>
    }
  />
));
LanguageSelector.displayName = 'LanguageSelector';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CultureTreeSelect = ({
  label,
  endpoint: _endpoint, // eslint-disable-line no-unused-vars -- Ignored, we use the new optimized API
  placeholder,
  isMulti = false,
  value,
  onChange,
  style,
  checkStrictly = false,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = DEFAULTS.LANGUAGE,
  showLanguageSelector = false,
  onLanguageChange = null,
  maxCount = null,
  maxTagCount = null,
  treeCheckable = true,
  enrichedCultures = null, // For backward compatibility (URL restore)
  filterParams = EMPTY_FILTER_PARAMS, // Additional filter parameters to pass to the API (e.g., { show_in_customer_filter_pdf: true })
  showPfiTag = true, // IS-27042: the risk calculator must not show any tag beside culture names
}) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();

  // ============================================================================
  // STATE
  // ============================================================================
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return defaultLanguage;
    const saved = localStorage.getItem(`lang_calculator_culture`);
    return saved || defaultLanguage;
  });

  // Refs
  const searchDebounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Rule 23 (SP-408): when the user picks a culture from a search suggestion
  // we expand its ancestors (below) AND scroll the dropdown to bring the
  // freshly-picked leaf into view. This pair of refs drives the scroll —
  // setting `scrollToCultureKeyRef` and then bumping `scrollToCultureTickRef`
  // schedules the effect that performs the actual scrollIntoView.
  const scrollToCultureKeyRef = useRef(null);
  const [scrollToCultureTick, setScrollToCultureTick] = useState(0);
  // IS-27042 (client rule "Cultures" pt 2): reopening the dropdown must land
  // on the last picked culture — remember it for handleDropdownVisibleChange.
  const lastPickedKeyRef = useRef(null);
  const prevValuesRef = useRef([]);
  const valueRef = useRef(value);
  valueRef.current = value;

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const languageOptions = useMemo(() => {
    if (!languageLabels) return [];
    return Object.entries(languageLabels).map(([code, lbl]) => ({
      value: code,
      label: lbl,
    }));
  }, [languageLabels]);

  // Build a flat map for O(1) label lookup from tree data
  const nodeMap = useMemo(() => {
    const map = new Map();

    const addNodes = (nodes) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        const id = node.value || node.id;
        if (id) map.set(id, node);
        if (node.children?.length > 0) addNodes(node.children);
      });
    };

    // Add from tree data
    addNodes(treeData);

    // Add from search results if any
    if (searchResults) {
      searchResults.forEach((node) => {
        const id = node.value || node.id;
        if (id && !map.has(id)) map.set(id, node);
      });
    }

    // Add from enrichedCultures (backward compatibility)
    if (enrichedCultures?.length > 0) {
      enrichedCultures.forEach((culture) => {
        const id = culture.value || culture.id;
        if (id && !map.has(id)) {
          map.set(id, {
            ...culture,
            value: id,
            title: culture.title || culture.label || culture.french_name || id,
          });
        }
      });
    }

    return map;
  }, [treeData, searchResults, enrichedCultures]);

  // Rule 23/R28 (SP-408): build a child→parent map from the full tree so we
  // can auto-expand ancestors after a suggestion click. We also seed it
  // from `searchResults` — the search response returns each culture (and
  // its ancestors) with a `parent_id` field, which is the only way to walk
  // up the chain for cultures that weren't yet loaded in the full tree.
  const parentMap = useMemo(() => {
    const map = new Map();
    const walk = (nodes, parentId) => {
      if (!Array.isArray(nodes)) return;
      for (const node of nodes) {
        const id = node.value || node.id;
        if (id && parentId) map.set(id, parentId);
        if (node.children?.length > 0) walk(node.children, id);
      }
    };
    walk(treeData, null);

    // Seed from search results: each row carries its own `parent_id`.
    if (Array.isArray(searchResults)) {
      for (const node of searchResults) {
        const id = node?.value || node?.id;
        const parentId = node?.parent_id;
        if (id && parentId && !map.has(id)) {
          map.set(id, parentId);
        }
      }
    }
    return map;
  }, [treeData, searchResults]);

  // ============================================================================
  // API HELPERS
  // ============================================================================
  const fetchFromAPI = useCallback(async (params, signal) => {
    const url = new URL(CALCULATOR_TREE_API, window.location.origin);

    // Add base params
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        url.searchParams.set(key, val);
      }
    });

    // Add filter params
    Object.entries(filterParams).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        url.searchParams.set(key, val);
      }
    });

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result = await response.json();
    return result.data || [];
  }, [filterParams]);

  // ============================================================================
  // LOAD FULL TREE (on mount)
  // ============================================================================
  const loadFullTree = useCallback(async () => {
    // Check cache first (include filterParams in cache key)
    const cached = getCachedFullTree(selectedLanguage, filterParams);
    if (cached) {
      setTreeData(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const data = await fetchFromAPI(
        { mode: 'full', lang: selectedLanguage },
        abortControllerRef.current.signal
      );

      setTreeData(data);
      setCachedFullTree(selectedLanguage, filterParams, data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage, filterParams, fetchFromAPI]);

  // Load full tree on mount and language change
  useEffect(() => {
    loadFullTree();
  }, [loadFullTree]);

  // ============================================================================
  // SEARCH (backend search with debounce)
  // ============================================================================
  const performSearch = useCallback(async (term) => {
    if (!term || term.length < CONFIG.MIN_SEARCH_LENGTH) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    try {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const data = await fetchFromAPI(
        { mode: 'search', search: term, lang: selectedLanguage },
        abortControllerRef.current.signal
      );

      setSearchResults(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setSearchResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  }, [selectedLanguage, fetchFromAPI]);

  // Debounced search handler
  const handleSearch = useCallback((term) => {
    setSearchValue(term);

    // Clear previous debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!term || term.length < CONFIG.MIN_SEARCH_LENGTH) {
      setSearchResults(null);
      return;
    }

    // Debounce the search
    searchDebounceRef.current = setTimeout(() => {
      performSearch(term);
    }, CONFIG.SEARCH_DEBOUNCE);
  }, [performSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleChange = useCallback((selectedValues) => {
    if (!onChange) return;

    const validValues = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues].filter(Boolean);

    // Build enriched nodes from nodeMap
    const enrichedNodes = validValues.map((val) => {
      const node = nodeMap.get(val);
      return {
        value: val,
        label: node?.title || val,
        key: val,
        title: node?.title || val,
        french_name: node?.french_name,
        english_name: node?.english_name,
        latin_name: node?.latin_name,
        ...(node || {}),
      };
    });

    onChange(validValues, enrichedNodes);
    // Checked keys come back in tree order, not pick order: diff against
    // the previous selection to find what was actually added.
    const prevSet = new Set(prevValuesRef.current);
    const added = validValues.filter((v) => !prevSet.has(v));
    if (added.length > 0) {
      lastPickedKeyRef.current = added[added.length - 1];
    } else if (!validValues.includes(lastPickedKeyRef.current)) {
      lastPickedKeyRef.current = null;
    }
    prevValuesRef.current = validValues;

    // Clear search after selection
    if (searchValue) {
      setSearchValue('');
      setSearchResults(null);
    }

    // Rule 23: when a culture is picked from the search suggestion list,
    // auto-expand its ancestor chain AND scroll the dropdown to bring the
    // newly-picked leaf into view, so the user lands on the culture in the
    // full tree instead of an empty collapsed view.
    if (searchValue && validValues.length > 0) {
      const newlyPicked = validValues[validValues.length - 1];
      const ancestors = [];
      // Start from the picked node's parent — the picked node itself is
      // always a leaf, so we walk upwards. If `parentMap` doesn't know the
      // parent (e.g. the leaf came from a search result that wasn't in the
      // full tree), fall back to the node's own `parent_id`.
      const seedNode = nodeMap.get(newlyPicked);
      let cursor =
        parentMap.get(newlyPicked) || seedNode?.parent_id || null;
      while (cursor && !ancestors.includes(cursor)) {
        ancestors.push(cursor);
        const parentNode = nodeMap.get(cursor);
        cursor = parentMap.get(cursor) || parentNode?.parent_id || null;
      }
      if (ancestors.length > 0) {
        setExpandedKeys((prev) => {
          const merged = new Set([...prev, ...ancestors]);
          return Array.from(merged);
        });
      }
      // Always queue a scroll, even when the culture is top-level (no
      // ancestors) — the dropdown still needs to scroll to it.
      scrollToCultureKeyRef.current = newlyPicked;
      setScrollToCultureTick((n) => n + 1);
    }
  }, [onChange, nodeMap, searchValue, parentMap]);

  const handleLanguageChange = useCallback((lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem(`lang_calculator_culture`, lang);
    setSearchValue('');
    setSearchResults(null);

    // Rule 16 (SP-408): switching language must NOT drop selections — the
    // MA/PC searches keep them and the Risk Calculator must behave the same.
    // The tree refetch below (via loadFullTree effect on selectedLanguage
    // change) will repopulate nodeMap with the new-language titles, so the
    // selected tags automatically pick up translated labels on next render.
    if (onLanguageChange) setTimeout(onLanguageChange, 100);
  }, [onLanguageChange]);

  const handleDropdownVisibleChange = useCallback((visible) => {
    setDropdownOpen(visible);
    if (!visible) {
      // Clear search when dropdown closes
      setSearchValue('');
      setSearchResults(null);
      return;
    }
    // Reopen positioned on the last picked culture (if still selected).
    const last = lastPickedKeyRef.current;
    const v = valueRef.current;
    const current = Array.isArray(v) ? v : [v];
    if (last && current.includes(last)) {
      scrollToCultureKeyRef.current = last;
      setScrollToCultureTick((n) => n + 1);
    }
  }, []);

  // ============================================================================
  // TAG RENDER (resolve labels for selected items)
  // ============================================================================
  const tagRender = useCallback((props) => {
    const { label: tagLabel, value: tagValue, closable, onClose } = props;

    // Try to get proper label from nodeMap
    const node = nodeMap.get(tagValue);
    const displayLabel = node?.title || tagLabel || tagValue;

    return (
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {displayLabel}
      </Tag>
    );
  }, [nodeMap]);

  // ============================================================================
  // FILTER (client-side filter for tree display)
  // ============================================================================
  const filterTreeNode = useCallback((inputValue, treeNode) => {
    // When using backend search, don't filter client-side
    if (searchResults !== null) return true;

    if (!inputValue) return true;

    const searchLower = inputValue.toLowerCase();
    const title = String(treeNode.title || '').toLowerCase();
    const frenchName = String(treeNode.french_name || '').toLowerCase();
    const englishName = String(treeNode.english_name || '').toLowerCase();
    const latinName = String(treeNode.latin_name || '').toLowerCase();

    return (
      title.includes(searchLower) ||
      frenchName.includes(searchLower) ||
      englishName.includes(searchLower) ||
      latinName.includes(searchLower)
    );
  }, [searchResults]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const notFoundContent = useMemo(() => {
    if (error) return error;
    if (loading || searchLoading) return <Spin size="small" />;
    if (searchResults !== null && searchResults.length === 0) {
      return t('No results found');
    }
    return t('No options found');
  }, [error, loading, searchLoading, searchResults, t]);

  const switcherIcon = useCallback(({ expanded }) => (
    expanded
      ? <ChevronDown size={CONFIG.CHEVRON_SIZE} />
      : <ChevronRight size={CONFIG.CHEVRON_SIZE} />
  ), []);

  // Custom title renderer for styling parent nodes differently.
  // Rule 21 (SP-408) — actual click-to-expand behaviour is handled by the
  // `treeExpandAction="click"` prop on TreeSelect below; this renderer
  // only needs to make parent titles look clickable.
  // The `data-tree-key` attribute lets the post-pick effect locate the
  // freshly-selected leaf in the rendered dropdown to scroll it into view.
  // SP-430 R22: in single-choice mode, mark the currently selected culture so
  // its radio button renders filled.
  const selectedValueSet = useMemo(() => {
    if (value === null || value === undefined) return new Set();
    const arr = Array.isArray(value) ? value : [value];
    return new Set(arr.filter(Boolean).map(String));
  }, [value]);

  const treeTitleRender = useCallback((node) => {
    // Non-selectable nodes are navigation parents → keep the bold green title.
    if (!node.selectable) {
      return (
        <span
          data-tree-key={node.value}
          style={{
            color: '#207D55',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '125%',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {node.title}
        </span>
      );
    }

    // SP-430 R22: a selectable choice shows a radio button (single-choice mode)
    // for clarity — filled when it is the current selection.
    const isSelected = selectedValueSet.has(String(node.value));
    return (
      <span
        data-tree-key={node.value}
        // SP-430 M39: marks the selected leaf so CSS can highlight its full row
        // identically in both modes (Rapide single-choice and Multichoix
        // checkable) — AntD's native selected background is only an indented
        // content-wrapper tint, which made the two screens look different.
        data-selected={isSelected ? 'true' : undefined}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {!isMulti && (
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: `2px solid ${isSelected ? '#207D55' : '#bfbfbf'}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isSelected && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#207D55',
                }}
              />
            )}
          </span>
        )}
        {node.title}
        {showPfiTag && node.is_pfi && node.name_to_display_in_the_icon && (
          <span
            style={{
              backgroundColor: '#207D55',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: '4px',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {node.name_to_display_in_the_icon}
          </span>
        )}
        {/* R25 (SP-408): when the user typed a synonym, display it in gray
            on the right of the culture name so they can confirm the match. */}
        {node.matched_synonym && (
          <span
            style={{
              color: '#8c8c8c',
              fontSize: '12px',
              fontStyle: 'italic',
              marginLeft: 'auto',
              paddingLeft: 8,
              whiteSpace: 'nowrap',
            }}
          >
            Synonyme&nbsp;: {node.matched_synonym}
          </span>
        )}
      </span>
    );
  }, [isMulti, selectedValueSet, showPfiTag]);

  // Rule 22 follow-up: with `overflow.adjustY: false` (set on the TreeSelect
  // below) AntD's rc-trigger no longer re-anchors the popup when the selector
  // grows after a selection or when the tree expands. The popup stays glued
  // to its initial `top`, visibly overlapping the selector. A user-driven
  // scroll fires rc-trigger's scroll listener and re-anchors it — so we
  // simulate that scroll programmatically by dispatching a `resize` event on
  // every change that affects the selector or popup geometry.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => cancelAnimationFrame(id);
  }, [value, expandedKeys, searchResults]);

  // Rule 23 (SP-408): after picking from search and expanding the ancestor
  // chain, scroll the freshly-picked leaf into view inside the dropdown.
  // AntD's TreeSelect re-renders asynchronously when expandedKeys grows, so
  // we poll on requestAnimationFrame until the node appears in the DOM, then
  // scroll the dropdown's list container only (and keep it there, see below).
  useEffect(() => {
    if (!scrollToCultureTick || !dropdownOpen || searchValue) return undefined;
    const targetKey = scrollToCultureKeyRef.current;
    if (!targetKey) return undefined;

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    let stableFrames = 0;
    // rc-virtual-list re-syncs scrollTop from its own state a few frames
    // after an external write, so a single write gets undone: keep
    // re-applying until the target stays visible, and stop as soon as the
    // user scrolls/clicks/types so we never fight them.
    const MAX_SCROLL_FRAMES = 60;
    const STABLE_FRAMES_REQUIRED = 3;
    const escapedKey = CSS.escape(String(targetKey));
    const abort = () => { cancelled = true; };
    const userEvents = ['wheel', 'touchmove', 'pointerdown'];
    let listenedList = null;

    const tryScroll = () => {
      if (cancelled) return;
      const dropdown = window.document.querySelector('.culture-tree-select-dropdown');

      if (dropdown) {
        const target = dropdown.querySelector(`[data-tree-key="${escapedKey}"]`);
        const list =
          dropdown.querySelector('.ant-select-tree-list-holder') ||
          dropdown.querySelector('.ant-select-tree-list-holder-inner') ||
          dropdown.querySelector('.ant-select-tree') ||
          dropdown;
        if (target && list && typeof list.scrollTop === 'number') {
          if (listenedList !== list) {
            listenedList?.removeEventListener && userEvents.forEach((e) => listenedList.removeEventListener(e, abort));
            userEvents.forEach((e) => list.addEventListener(e, abort, { passive: true }));
            listenedList = list;
          }
          const listRect = list.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const visible =
            targetRect.top >= listRect.top && targetRect.bottom <= listRect.bottom;
          if (visible) {
            stableFrames += 1;
            if (stableFrames >= STABLE_FRAMES_REQUIRED) {
              scrollToCultureKeyRef.current = null;
              return;
            }
          } else {
            stableFrames = 0;
            const offset =
              targetRect.top -
              listRect.top -
              listRect.height / 2 +
              targetRect.height / 2;
            list.scrollTop = list.scrollTop + offset;
          }
        }
      }

      attempts += 1;
      if (attempts < MAX_SCROLL_FRAMES) {
        rafId = requestAnimationFrame(tryScroll);
      } else {
        scrollToCultureKeyRef.current = null;
      }
    };

    window.document.addEventListener('keydown', abort, true);
    rafId = requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      window.document.removeEventListener('keydown', abort, true);
      if (listenedList) userEvents.forEach((e) => listenedList.removeEventListener(e, abort));
    };
  }, [scrollToCultureTick, dropdownOpen, searchValue]);

  // Transform tree data to disable checkboxes on parent nodes
  const transformTreeData = useCallback((nodes) => {
    if (!nodes || !Array.isArray(nodes)) return nodes;

    return nodes.map((node) => {
      const hasChildren = (node.children && node.children.length > 0) || node.has_children === true || node.isLeaf === false;

      // SP-430 R21: respect the backend `selectable` flag when provided. In the
      // gated treatment mode a culture is selectable iff it has its own valid
      // treatment (e.g. "Plantes ornementales") even when it also has children.
      // Outside that mode the backend sends selectable = leaf-only, so behaviour
      // is unchanged for the MA/PC/calculator callers.
      const backendSelectable =
        typeof node.selectable === 'boolean' ? node.selectable : !hasChildren;

      return {
        ...node,
        checkable: backendSelectable,
        disableCheckbox: !backendSelectable,
        selectable: backendSelectable,
        // Recursively transform children
        children: hasChildren ? transformTreeData(node.children) : node.children,
      };
    });
  }, []);

  // Rule 22 (SP-408): the suggestion list produced by a search must contain
  // only selectable (leaf) cultures — parents are hidden.
  //
  // Important subtlety: the search endpoint returns parent-category rows
  // (e.g. "Arbres fruitiers", "Cultures maraîchères et fruitières") with
  // `has_children: true` but no inline `children[]` array. Those rows
  // would slip through a plain `children.length === 0` check. We treat a
  // node as a leaf only when it has NO child array AND is not flagged as
  // a non-leaf via `has_children` / `isLeaf === false`.
  const isLeafNode = useCallback((node) => {
    if (!node) return false;
    const childList = Array.isArray(node.children) ? node.children : [];
    if (childList.length > 0) return false;
    if (node.has_children === true) return false;
    if (node.isLeaf === false) return false;
    return true;
  }, []);

  const flattenToLeaves = useCallback((nodes) => {
    const out = [];
    const seen = new Set();
    const walk = (list) => {
      if (!Array.isArray(list)) return;
      for (const node of list) {
        const childList = Array.isArray(node?.children) ? node.children : [];
        if (childList.length > 0) {
          walk(childList);
          // Also skip the current node: it is a parent in the hierarchy,
          // never surfaced in the suggestion list.
          continue;
        }
        if (!isLeafNode(node)) {
          continue;
        }
        const id = node?.value || node?.id;
        if (id && !seen.has(id)) {
          seen.add(id);
          out.push({ ...node, children: undefined });
        }
      }
    };
    walk(nodes);
    return out;
  }, [isLeafNode]);

  // Use search results (flattened to leaves) when available.
  // While the user is still typing and `searchResults` is empty (the 300ms
  // debounce hasn't fired or returned yet), the fallback used to be the
  // full tree — which shows ancestor branches and therefore parent rows,
  // violating rule 22. Flatten the local tree immediately so leaves-only
  // is the rule the moment the user starts typing.
  const displayTreeData = useMemo(() => {
    if (searchResults !== null) {
      return transformTreeData(flattenToLeaves(searchResults));
    }
    if (searchValue && searchValue.trim().length > 0) {
      const needle = searchValue.trim().toLowerCase();
      const matchingLeaves = flattenToLeaves(treeData).filter((leaf) => {
        const fr = String(leaf.french_name || leaf.title || '').toLowerCase();
        const en = String(leaf.english_name || '').toLowerCase();
        const la = String(leaf.latin_name || '').toLowerCase();
        return fr.includes(needle) || en.includes(needle) || la.includes(needle);
      });
      return transformTreeData(matchingLeaves);
    }
    return transformTreeData(treeData);
  }, [searchResults, searchValue, treeData, transformTreeData, flattenToLeaves]);

  // SP-430 R40: pressing Enter in the search box selects the FIRST suggestion.
  // While searching, displayTreeData is a flat list of selectable leaves, so
  // the first selectable entry is the top suggestion shown to the user.
  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter') return;
      if (!searchValue || !searchValue.trim()) return;
      const firstSuggestion = (displayTreeData || []).find(
        (n) => n && n.selectable !== false
      );
      if (!firstSuggestion) return;
      e.preventDefault();
      if (isMulti) {
        const current = Array.isArray(value) ? value : [];
        if (!current.includes(firstSuggestion.value)) {
          handleChange([...current, firstSuggestion.value]);
        }
      } else {
        handleChange(firstSuggestion.value);
      }
    },
    [searchValue, displayTreeData, isMulti, value, handleChange]
  );

  // ============================================================================
  // MOBILE HELPERS
  // ============================================================================
  // Get display text for mobile trigger
  const mobileDisplayValue = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return '';
    }

    const valueArray = Array.isArray(value) ? value : [value];

    if (isMulti) {
      const count = valueArray.length;
      if (count === 0) return '';
      if (count === 1) {
        const node = nodeMap.get(valueArray[0]);
        return node?.title || valueArray[0];
      }
      return t('{{count}} sélectionné(s)', { count });
    }

    const node = nodeMap.get(valueArray[0]);
    return node?.title || valueArray[0];
  }, [value, nodeMap, isMulti, t]);

  // Language selector for mobile sheet
  const mobileLanguageSelector = useMemo(() => {
    if (!showLanguageSelector || !languageFields || !languageLabels) return null;

    return (
      <LanguageSelector
        value={selectedLanguage}
        options={languageOptions}
        onChange={handleLanguageChange}
        langLabel={t('Langue')}
      />
    );
  }, [showLanguageSelector, languageFields, languageLabels, selectedLanguage, languageOptions, handleLanguageChange]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="select-header flex items-center justify-between">
          {label && (
            <label className="block text-sm font-medium text-gray-700">
              {t(label)}
            </label>
          )}
          {showLanguageSelector && languageFields && languageLabels && (
            <LanguageSelector
              value={selectedLanguage}
              options={languageOptions}
              onChange={handleLanguageChange}
              langLabel={t('Langue')}
            />
          )}
        </div>

        {/* Mobile trigger - styled like a select */}
        <div
          onClick={() => setSheetOpen(true)}
          style={{
            width: '100%',
            minHeight: 44,
            padding: '8px 12px',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            backgroundColor: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: mobileDisplayValue ? '#262626' : '#bfbfbf',
            ...(style || {}),
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {mobileDisplayValue || placeholder || t('Select an option')}
          </span>
          {loading ? (
            <Spin size="small" style={{ marginLeft: 8 }} />
          ) : (
            <ChevronDown size={16} style={{ marginLeft: 8, color: '#bfbfbf', flexShrink: 0 }} />
          )}
        </div>

        {/* Mobile tree sheet */}
        <MobileTreeSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={label ? t(label) : placeholder}
          treeData={displayTreeData}
          value={Array.isArray(value) ? value : (value ? [value] : [])}
          onChange={handleChange}
          isMulti={isMulti}
          loading={loading || searchLoading}
          onSearch={handleSearch}
          maxCount={maxCount}
          languageSelector={mobileLanguageSelector}
        />
      </div>
    );
  }

  // Desktop render (unchanged)
  return (
    <div className="w-full space-y-2">
      <style>
        {`
          .culture-tree-select-dropdown .ant-select-tree-checkbox-checked .ant-select-tree-checkbox-inner {
            background-color: #207D55 !important;
            border-color: #207D55 !important;
          }
          .culture-tree-select-dropdown .ant-select-tree-checkbox-indeterminate .ant-select-tree-checkbox-inner::after {
            background-color: #207D55 !important;
          }
          .culture-tree-select-dropdown .ant-select-tree-checkbox:hover .ant-select-tree-checkbox-inner {
            border-color: #207D55 !important;
          }
          .culture-tree-select-dropdown .ant-select-tree-checkbox-checked::after {
            border-color: #207D55 !important;
          }
          /* SP-430 M39: a selected choice must "s'afficher en couleur" when the
             dropdown is reopened. Highlight the WHOLE ROW with a light green
             background, IDENTICALLY in Rapide (single-choice) and Multichoix
             (checkable). Driven by data-selected (set in treeTitleRender on the
             rendered leaf) so the width is the same full row in both modes and
             does not depend on AntD internals; the checkbox-checked selector is
             kept as a belt-and-braces match for Multichoix. Indeterminate
             (partially-checked) parents use a different class → not matched. */
          .culture-tree-select-dropdown .ant-select-tree-treenode:has([data-selected="true"]),
          .culture-tree-select-dropdown .ant-select-tree-treenode:has(.ant-select-tree-checkbox-checked) {
            background-color: #f0f9e8 !important;
          }
          /* Neutralise AntD's native indented selected tint (single-choice
             Rapide) so only the full-row highlight above shows — otherwise
             Rapide kept a narrower content-wrapper band (the width mismatch
             reported 16 juin 2026). */
          .culture-tree-select-dropdown .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected {
            background-color: transparent !important;
          }
          /* Rule 30 (SP-408): the arrow must stay visible after a selection
             so the user can reopen the dropdown without accidentally
             clearing all their selections; the X needs to be large enough
             to click reliably. Ant Select hides the arrow by default once
             allowClear has a value — undo that. */
          .culture-tree-select .ant-select-arrow {
            opacity: 1 !important;
            visibility: visible !important;
            display: inline-flex !important;
            right: 34px !important;
          }
          .culture-tree-select .ant-select-clear {
            opacity: 1 !important;
            visibility: visible !important;
            font-size: 18px !important;
            width: 18px !important;
            height: 18px !important;
            margin-top: -9px !important;
            right: 11px !important;
          }
        `}
      </style>
      <div className="select-header flex items-center justify-between">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {t(label)}
          </label>
        )}
        {showLanguageSelector && languageFields && languageLabels && (
          <LanguageSelector
            value={selectedLanguage}
            options={languageOptions}
            onChange={handleLanguageChange}
            langLabel={t('Langue')}
          />
        )}
      </div>

      <TreeSelect
        // Re-mount only when the language switches (so labels refresh). We
        // intentionally do NOT include `nodeMap.size` here — when the user
        // picks their first culture, nodeMap grows (the freshly-selected
        // node is enriched), the key would change, and the whole TreeSelect
        // would remount mid-interaction. That's what caused the popup to
        // re-anchor on top of the search bar (R22, SP-408, 14 mai 2026).
        key={selectedLanguage}
        value={value}
        onChange={handleChange}
        // SP-430 R40: Enter selects the first suggestion.
        onInputKeyDown={handleInputKeyDown}
        treeData={displayTreeData}
        // AntD TreeSelect uses treeExpandedKeys / onTreeExpand (NOT
        // expandedKeys / onExpand — those are Tree-only props and get
        // silently ignored on TreeSelect, which is why the Rule 23
        // auto-expand was never visible).
        treeExpandedKeys={expandedKeys}
        onTreeExpand={setExpandedKeys}
        // Rule 21 (SP-408): clicking anywhere on a non-leaf row (not just
        // the chevron) toggles its subtree. Ant leaves this off by default;
        // setting it to 'click' is the recommended toggle-on-title path.
        treeExpandAction="click"
        open={dropdownOpen}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        // R22 (SP-408): keep the popup anchored BELOW the selector. Without
        // this, when the selector grows (after first tag is added) AntD's
        // auto-adjust flips the popup above and the search bar becomes
        // unreachable. `adjustY: false` disables the vertical flip.
        placement="bottomLeft"
        popupAlign={{ overflow: { adjustY: false } }}
        treeNodeFilterProp="title"
        fieldNames={{
          label: 'title',
          value: 'value',
          children: 'children',
          key: 'value',
        }}
        switcherIcon={switcherIcon}
        treeTitleRender={treeTitleRender}
        placeholder={placeholder || t('Select an option')}
        loading={loading || searchLoading}
        showSearch
        allowClear
        multiple={isMulti}
        treeDefaultExpandAll={false}
        style={style}
        className="culture-tree-select"
        dropdownStyle={{
          maxHeight: CONFIG.DROPDOWN_MAX_HEIGHT,
          overflow: 'auto',
          padding: CONFIG.DROPDOWN_PADDING,
        }}
        // AntD v5 renamed dropdownClassName → popupClassName. Keep both for
        // belt-and-braces (popupClassName is what actually wins on 5.x).
        dropdownClassName="culture-tree-select-dropdown"
        popupClassName="culture-tree-select-dropdown"
        searchValue={searchValue}
        onSearch={handleSearch}
        filterTreeNode={filterTreeNode}
        notFoundContent={notFoundContent}
        treeCheckable={isMulti && treeCheckable}
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        popupMatchSelectWidth={false}
        // Rule 23 (SP-408): the post-pick scroll-into-view effect reads the
        // dropdown DOM to locate the chosen leaf. AntD's virtual list only
        // renders nodes inside the current viewport, so the leaf may not
        // exist in the DOM yet. Turning virtual off keeps every expanded
        // branch in the DOM — the tree is bounded (~hundreds of leaves
        // after the calculator filter) so the cost is negligible.
        virtual={false}
        checkStrictly={isMulti && checkStrictly}
        tagRender={isMulti ? tagRender : undefined}
        {...(isMulti && maxCount ? { maxCount } : {})}
        {...(maxTagCount != null ? {
          maxTagCount,
          maxTagPlaceholder: (omittedValues) =>
            `+${omittedValues.length} ${omittedValues.length > 1 ? 'sélections' : 'sélection'}`,
        } : {})}
      />

      {/*
       * Rule 25/27 (SP-408): render every selected culture as a removable
       * tag BELOW the search bar. The bar itself still collapses to
       * "+X sélections" (rule 27), but this list gives the user a full
       * overview and a direct remove action per culture — same pattern as
       * the MA/PC search filters.
       */}
      {isMulti && Array.isArray(value) && value.length > 0 && (
        <div
          className="culture-tree-select-tags"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 6,
          }}
        >
          {value.map((val) => {
            const node = nodeMap.get(val);
            const displayLabel = node?.title || val;
            return (
              <Tag
                key={val}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  const next = value.filter((v) => v !== val);
                  handleChange(next);
                }}
                style={{ marginRight: 0 }}
              >
                {displayLabel}
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default React.memo(CultureTreeSelect);
